import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, CircularProgress, Container, Grid, Paper,
  Stack, TextField, Typography,
} from "@mui/material";
import { MdCloudUpload, MdLink, MdSave } from "react-icons/md";
import Colors from "../../assets/styles/Colors";
import { getResumenCard, setResumenCard } from "../store/actions/resumenConfig";

const HERO_CARD = {
  key: "main_card",
  label: "Imagen Principal (Hero)",
  appSection: "Resumen › Banner superior de la pantalla",
  recommendation: "Imagen de fondo del banner grande. Recomendado: 1400×300 px.",
};

const CARDS = [
  {
    key: "link_card",
    label: "Tarjeta de Link",
    appSection: "Resumen › COMPARTIR LINK",
    recommendation: "Imagen de fondo. Recomendado: 400×220 px.",
  },
  {
    key: "app_card",
    label: "Tarjeta de App",
    appSection: "Resumen › DESCARGAR APP",
    recommendation: "Imagen de fondo. Recomendado: 400×220 px.",
  },
  {
    key: "saldo_card",
    label: "Tarjeta de Saldo",
    appSection: "Resumen › SALDO DISPONIBLE",
    recommendation: "Imagen de fondo. Recomendado: 400×220 px.",
  },
];

// ── HeroCardPanel ─────────────────────────────────────────────────────────────

function HeroCardPanel({ card }) {
  const [imageURL, setImageURL] = useState("");
  const [file, setFile]         = useState(null);
  const [preview, setPreview]   = useState(null);
  const [saving, setSaving]     = useState(false);
  const [status, setStatus]     = useState("");
  const fileRef = useRef();

  const load = useCallback(async () => {
    const data = await getResumenCard(card.key);
    if (data?.imageURL) {
      setImageURL(data.imageURL);
      setPreview(data.imageURL);
    }
  }, [card.key]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { return () => { if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview); }; }, [preview]);

  const setSelectedFile = (f) => {
    if (!f) return;
    if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus("");
    try {
      await setResumenCard(card.key, { label: "", clickMe: "", imageURL }, file);
      setFile(null);
      await load();
      setStatus("saved");
      setTimeout(() => setStatus(""), 3000);
    } catch {
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      {/* Wide preview drop zone */}
      <Box
        onDrop={(e) => { e.preventDefault(); setSelectedFile(e.dataTransfer.files?.[0]); }}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileRef.current.click()}
        sx={{
          border: `2px dashed ${Colors.borderPrimary}`,
          borderRadius: 2,
          height: 200,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          overflow: "hidden",
          backgroundColor: Colors.bgTertiary,
          transition: "border-color 160ms, background 160ms",
          "&:hover": { borderColor: Colors.primary, backgroundColor: Colors.bgElevated },
        }}
      >
        {preview ? (
          <img src={preview} alt="Hero" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <Stack alignItems="center" spacing={0.75}>
            <MdCloudUpload size={28} color={Colors.textSecondary} />
            <Typography variant="body2" sx={{ color: Colors.textSecondary }}>Arrastra o haz clic para subir la imagen del banner principal</Typography>
            <Typography variant="caption" sx={{ color: Colors.textMuted }}>{card.recommendation}</Typography>
          </Stack>
        )}
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
          onChange={(e) => setSelectedFile(e.target.files?.[0])} />
      </Box>

      <Stack direction="row" alignItems="center" spacing={2}>
        {status === "saved" && <Typography variant="caption" sx={{ color: Colors.success, fontWeight: 600 }}>Imagen guardada correctamente.</Typography>}
        {status === "error" && <Typography variant="caption" sx={{ color: Colors.error }}>Error al guardar. Intenta de nuevo.</Typography>}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || (!file && !imageURL)}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: Colors.white }} /> : <MdSave />}
          sx={{ backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2 }}
        >
          {saving ? "Guardando..." : "Guardar imagen"}
        </Button>
      </Stack>
    </Stack>
  );
}

// ── CardPanel ─────────────────────────────────────────────────────────────────

function CardPanel({ card }) {
  const [form, setForm]       = useState({ label: "", clickMe: "", imageURL: "" });
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [status, setStatus]   = useState(""); // "saved" | "error" | ""
  const fileRef = useRef();

  const load = useCallback(async () => {
    const data = await getResumenCard(card.key);
    if (data) {
      setForm({ label: data.label || "", clickMe: data.clickMe || "", imageURL: data.imageURL || "" });
      setPreview(data.imageURL || null);
    }
  }, [card.key]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { return () => { if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview); }; }, [preview]);

  const handle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const setSelectedFile = (f) => {
    if (!f) return;
    if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus("");
    try {
      await setResumenCard(card.key, form, file);
      setFile(null);
      await load();
      setStatus("saved");
      setTimeout(() => setStatus(""), 3000);
    } catch {
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ borderRadius: 3, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}`, overflow: "hidden", height: "100%" }}>
      <Box sx={{ px: 2.5, py: 1.75, backgroundColor: Colors.bgTertiary, borderBottom: `1px solid ${Colors.borderSecondary}` }}>
        <Typography variant="subtitle1" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>
          {card.label}
        </Typography>
        <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{card.appSection}</Typography>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          {/* Image drop zone */}
          <Box
            onDrop={(e) => { e.preventDefault(); setSelectedFile(e.dataTransfer.files?.[0]); }}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileRef.current.click()}
            sx={{
              border: `2px dashed ${Colors.borderPrimary}`,
              borderRadius: 2,
              height: 130,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              overflow: "hidden",
              backgroundColor: Colors.bgTertiary,
              transition: "border-color 160ms, background 160ms",
              "&:hover": { borderColor: Colors.primary, backgroundColor: Colors.bgElevated },
            }}
          >
            {preview ? (
              <img src={preview} alt={card.label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <Stack alignItems="center" spacing={0.75}>
                <MdCloudUpload size={22} color={Colors.textSecondary} />
                <Typography variant="caption" sx={{ color: Colors.textSecondary }}>Arrastra o haz clic</Typography>
              </Stack>
            )}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
              onChange={(e) => setSelectedFile(e.target.files?.[0])} />
          </Box>

          <TextField
            label="Etiqueta / Título" value={form.label} onChange={handle("label")}
            fullWidth size="small"
            sx={{ "& .MuiInputLabel-root": { color: Colors.textSecondary } }}
          />
          <TextField
            label="URL de destino" value={form.clickMe} onChange={handle("clickMe")}
            fullWidth size="small" placeholder="https://..."
            InputProps={{ startAdornment: <MdLink style={{ marginRight: 6, color: Colors.textSecondary }} /> }}
            sx={{ "& .MuiInputLabel-root": { color: Colors.textSecondary } }}
          />

          <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{card.recommendation}</Typography>

          {status === "saved" && (
            <Typography variant="caption" sx={{ color: Colors.success, fontWeight: 600 }}>Tarjeta guardada correctamente.</Typography>
          )}
          {status === "error" && (
            <Typography variant="caption" sx={{ color: Colors.error }}>Error al guardar. Intenta de nuevo.</Typography>
          )}

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} sx={{ color: Colors.white }} /> : <MdSave />}
            sx={{ alignSelf: "flex-end", backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2 }}
          >
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}

// ── ResumenConfigView ─────────────────────────────────────────────────────────

export default function ResumenConfigView() {
  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, background: `linear-gradient(135deg, ${Colors.bgSecondary} 0%, ${Colors.bgTertiary} 100%)`, border: `1px solid ${Colors.borderSecondary}` }}>
          <Typography variant="h5" sx={{ color: Colors.textPrimary }}>Tarjetas Resumen</Typography>
          <Typography variant="body2" sx={{ color: Colors.textSecondary, mt: 0.5 }}>
            Imágenes y links de las tarjetas en la pantalla de resumen del usuario
          </Typography>
        </Paper>

        {/* Hero image — full width, shown first */}
        <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>
              {HERO_CARD.label}
            </Typography>
            <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{HERO_CARD.appSection}</Typography>
          </Box>
          <HeroCardPanel card={HERO_CARD} />
        </Paper>

        {/* Three smaller cards */}
        <Grid container spacing={2.5}>
          {CARDS.map((card) => (
            <Grid item xs={12} lg={4} key={card.key}>
              <CardPanel card={card} />
            </Grid>
          ))}
        </Grid>

      </Container>
    </Box>
  );
}
